import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { InternalNoteBadgeMemoIcon } from "../../icon";
import { ConversationCardModel } from "../../models/conversations";
import { UserModel } from "../../models/users.model";
import * as selector from "../../selectors/selectors";
import { UserService } from "../../services/UserService";
import { UtilityService } from "../../services/UtilityService";
import { AutomationRulesLabel, Tags } from "../shared/constants";
import Mention from "../shared/mentions";

const TAG_PATTERN = /\{\{_(.+?)_\}\}/g;

function parseTeamNoteMessageToNodes(message: string, isDarkMode: boolean): React.ReactNode {
    if (!message) return null;

    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let key = 0;
    let match: RegExpExecArray | null;

    while ((match = TAG_PATTERN.exec(message)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(<span key={key++}>{message.slice(lastIndex, match.index)}</span>);
        }

        nodes.push(
            <span
                key={key++}
                className={`conv-history-team-note__tag d-inline-block br-4 ${
                    isDarkMode
                        ? "bg-color-internal-note-history-tag-bg color-internal-note-composer-mention-chip-bg"
                        : "bg-color-internal-note-composer-mention-chip-bg color-internal-note-text"
                }`}
            >
                {match[1]}
            </span>
        );

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < message.length) {
        nodes.push(<span key={key++}>{message.slice(lastIndex)}</span>);
    }

    return <>{nodes}</>;
}

function formatUserShortName(user: UserModel): string {
    const firstName = user.FirstName?.trim() || "";
    const lastName = user.LastName?.trim() || "";

    const name =
        firstName && lastName
            ? `${firstName} ${lastName.charAt(0).toUpperCase()}.`
            : firstName || lastName || user.Email?.split("@")[0] || "";

    return UtilityService.trimMessage(name, 24) || "";
}

function getTeamNoteSenderDisplayName(
    senderId: string | undefined,
    isOutbound: boolean,
    allUsers: UserModel[]
): string {
    const resolvedSenderId =
        (senderId || "").trim() || (isOutbound ? UserService.getCurrentUserId() || "" : "");

    if (!resolvedSenderId) return "";
    if (resolvedSenderId === AutomationRulesLabel.SENDERID) return AutomationRulesLabel.AUTOMATED_REPLY;

    const user = allUsers.find((x) => x.ID === resolvedSenderId);
    return user ? formatUserShortName(user) : "";
}

export type TeamNoteHistoryCardProps = {
    MessageData: any;
    CardData: ConversationCardModel;
    IsSecureChat?: boolean;
};

const TeamNoteHistoryCard = ({ MessageData }: TeamNoteHistoryCardProps) => {
    const IsDarkMode = useSelector(selector.isDarkMode);
    const IsMobileDevice = useSelector(selector.isMobileDevice);
    const AllUsers: UserModel[] = useSelector(selector.allUsers);

    const {
        Message = "",
        MessageDateTime,
        IsOutbound,
        SenderID,
        GroupChat,
    } = MessageData;

    const hasStructuredTags = /\{\{_.+?_\}\}/.test(Message);

    const senderDisplayName = useMemo(
        () => getTeamNoteSenderDisplayName(SenderID, IsOutbound, AllUsers),
        [SenderID, IsOutbound, AllUsers]
    );

    return (
        <div className={`conv-history-team-note-outer w-p-100 min-w-0 d-flex custom-border-box-size justify-center ${IsMobileDevice ? "px-2" : ""}`}>
            <div
                className="conv-history-team-note custom-border-box-size flex-grow-0 br-2 o-hidden bg-color-internal-note-history-bg b-around-internal-note-history-border d-flex p-relative"
                role="article"
                aria-label="Internal note"
            >
                <div className="conv-history-team-note__accent flex-shrink-0 bg-color-internal-note-history-accent" aria-hidden />

                <div className="flex-1 min-w-0 py-1 px-2">
                    <div className="conv-history-team-note__header mb-1 d-flex align-center justify-between">
                        <div className="conv-history-team-note__header-left flex-1 min-w-0 o-hidden d-flex align-center">
                            <InternalNoteBadgeMemoIcon className="flex-shrink-0 w-2 h-2 color-internal-note-history-accent" aria-hidden />

                            <div className="conv-history-team-note__meta flex-1 min-w-0 o-hidden h6-regular color-internal-note-history-meta d-flex">
                                <span className="conv-history-team-note__badge-label flex-shrink-0 font-bold color-internal-note-history-badge-label">
                                    INTERNAL NOTE
                                </span>

                                {senderDisplayName && (
                                    <>
                                        <span className="flex-shrink-0 opacity-p-65" aria-hidden>
                                            •
                                        </span>
                                        <span className="conv-history-team-note__sender flex-1 min-w-0 o-hidden w-space color-internal-note-history-sender">
                                            {senderDisplayName}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>

                        <span className="flex-shrink-0 w-space h6-regular color-internal-note-history-meta">
                            {UtilityService.convertToTimeFormat(MessageDateTime)}
                        </span>
                    </div>

                    <div className="conv-history-team-note__body h5-regular w-p-100 color-internal-note-history-body">
                        {hasStructuredTags ? (
                            parseTeamNoteMessageToNodes(Message, IsDarkMode)
                        ) : (
                            <Mention
                                IsGroupChat={!!GroupChat?.ID}
                                body={Message}
                                tags={Tags}
                                emoji=""
                                openTags={false}
                                tagTarget={undefined}
                                onChange={() => { }}
                                onKeyDown={() => { }}
                                closeTags={() => { }}
                                maxLength={Message.length}
                                id=""
                                placeholder=""
                                readOnly
                                IsOutbound={IsOutbound}
                                IsLinkAllowed
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamNoteHistoryCard;